import { Checkbox } from '@axo/ui-core/components/input/Checkbox';
import {
  InputSize,
  InputStateVariant,
} from '@axo/ui-core/components/input/InputProps.types';
import { Stack } from '@axo/ui-core/components/layout/item';
import { Text } from '@axo/ui-core/components/typography';
import clsx from 'clsx';
import {
  ChangeEvent,
  forwardRef,
  ForwardRefRenderFunction,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { IInputProps } from '../../models/input.model';
import styles from './CheckboxUI.module.scss';

export interface ICheckbox
  extends IInputProps<HTMLInputElement, boolean | undefined> {
  className?: string;
  children?: ReactNode;
  label?: string;
  size?: InputSize;
  error?: string;
  state?: InputStateVariant;
  testid?: string;
  isUsingInternalState?: boolean;
}

export const CheckboxUI: ForwardRefRenderFunction<
  HTMLButtonElement,
  ICheckbox
> = (
  {
    children = '',
    value,
    error,
    name = '',
    label,
    isUsingInternalState = true,
    onChange,
    className,
    testid,
    size = 'l',
    state = 'neutral',
  },
  ref
) => {
  const inputId = useId();
  const [isChecked, setIsChecked] = useState(value);

  useEffect(() => setIsChecked(value), [value]);

  const _onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setIsChecked(e.target.checked);
      onChange?.(e);
    },
    [onChange]
  );

  const checkedValue = useMemo(
    () => (isUsingInternalState ? isChecked : value),
    [isChecked, value, isUsingInternalState]
  );

  return (
    <label
      className={clsx(styles.checkbox, className)}
      htmlFor={inputId}
      data-error={!!error}
      data-checked={checkedValue}
      data-size={size}
      data-state={state}
      data-testid={testid}
    >
      <Stack direction={'h'} gap={'3xs'} align={'start'}>
        <Checkbox
          name={name}
          // value={value} // fyi/ string values, not boolean
          _ref={ref}
          id={inputId}
          size={size}
          state={state}
          checked={checkedValue ?? false}
          onChange={_onChange}
        />

        <div className={styles.column}>
          {label && <Text className={styles.label}>{label}</Text>}
          {children && (
            <Text as={'span'} className={styles.description}>
              {children}
            </Text>
          )}

          {error && (
            <Text className={styles.errorText} size={'xs'}>
              {error}
            </Text>
          )}
        </div>
      </Stack>
    </label>
  );
};

export default forwardRef(CheckboxUI);
